/**
 * ManagementDashboard.jsx
 * =======================
 *
 * Hospital-leadership cockpit:
 *   ▸ KPI tiles (visits / revenue / queue / schedule-Rx)
 *   ▸ Visits-per-day chart (Recharts)
 *   ▸ Doctor workload leaderboard (most ↔ least)
 *   ▸ Referrals by doctor / reason
 *   ▸ Schedule-drug Rx feed
 *   ▸ Today's roster
 *   ▸ AI Insights (Observations / Risks / Recommendations / Alerts)
 *   ▸ Period picker  +  Hospital PDF  +  Per-doctor PDF
 *
 * All renders read live from /api/management/*.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import DrawerShell from '../../components/DrawerShell';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Activity, FileBarChart, Pill, Users, AlertTriangle, ShieldAlert,
  TrendingUp, TrendingDown, Sparkles, ChevronRight, Clock, FileDown,
  Stethoscope, ScrollText, Calendar, LogOut, RefreshCw, IndianRupee,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDoctorName } from '../../utils/doctor';

const API = process.env.REACT_APP_BACKEND_URL;

// IST date string helpers
const istNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
const istDateStr = (d) => {
  const dt = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const RANGE_PRESETS = [
  { v: 'today',    l: 'Today',         days: 0 },
  { v: '7',        l: 'Last 7 days',   days: 7 },
  { v: '30',       l: 'Last 30 days',  days: 30 },
  { v: '90',       l: 'Last 90 days',  days: 90 },
  { v: '180',      l: 'Last 6 months', days: 180 },
  { v: '365',      l: 'Last year',     days: 365 },
];

function computeRange(preset) {
  const to = istNow();
  const from = istNow();
  const meta = RANGE_PRESETS.find(p => p.v === preset);
  from.setDate(from.getDate() - (meta?.days ?? 30));
  return { from: istDateStr(from), to: istDateStr(to), label: meta?.l ?? preset };
}

const KpiTile = ({ label, value, icon: Icon, hint, tone = 'blue', onClick }) => {
  const tones = {
    blue:   { bg: 'from-blue-600 to-indigo-700',   ring: 'ring-blue-200' },
    teal:   { bg: 'from-teal-600 to-emerald-700',  ring: 'ring-teal-200' },
    amber:  { bg: 'from-amber-500 to-orange-600',  ring: 'ring-amber-200' },
    rose:   { bg: 'from-rose-600 to-pink-700',     ring: 'ring-rose-200' },
    violet: { bg: 'from-violet-600 to-fuchsia-700',ring: 'ring-violet-200' },
  }[tone] || { bg: 'from-zinc-700 to-zinc-900', ring: 'ring-zinc-200' };
  return (
    <button
      onClick={onClick}
      className={`group text-left rounded-2xl border border-zinc-200 bg-white p-5 hover:shadow-lg transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 ${tones.ring}`}
      data-testid={`mgmt-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${tones.bg} text-white grid place-items-center shadow-sm`}>
          <Icon className="h-4 w-4" />
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-300 group-hover:text-zinc-500 group-hover:translate-x-0.5 transition" />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: 'Manrope' }}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </button>
  );
};

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function ManagementDashboard() {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const [preset, setPreset] = useState('30');
  const range = useMemo(() => computeRange(preset), [preset]);

  const [overview, setOverview]   = useState(null);
  const [workload, setWorkload]   = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [schedule, setSchedule]   = useState(null);
  const [roster, setRoster]       = useState(null);
  const [ai, setAi]               = useState(null);
  const [loading, setLoading]     = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [drawer, setDrawer]       = useState(null); // {kind, payload}

  const loadAll = async () => {
    setLoading(true);
    const q = `from=${range.from}&to=${range.to}`;
    try {
      const [o, w, r, s, rs] = await Promise.all([
        axios.get(`${API}/api/management/overview?${q}`,        { withCredentials: true }),
        axios.get(`${API}/api/management/doctors/workload?${q}`,{ withCredentials: true }),
        axios.get(`${API}/api/management/referrals?${q}`,       { withCredentials: true }),
        axios.get(`${API}/api/management/schedule-drugs?${q}`,  { withCredentials: true }),
        axios.get(`${API}/api/management/roster`,               { withCredentials: true }),
      ]);
      setOverview(o.data);
      setWorkload(w.data);
      setReferrals(r.data);
      setSchedule(s.data);
      setRoster(rs.data);
    } catch (e) {
      toast.error('Failed to load management data');
    }
    setLoading(false);
  };

  const loadAi = async () => {
    setAiLoading(true);
    try {
      const q = `from=${range.from}&to=${range.to}`;
      const { data } = await axios.get(`${API}/api/management/ai-insights?${q}`, { withCredentials: true });
      setAi(data);
    } catch (e) {
      toast.error('AI insights unavailable');
    }
    setAiLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [preset]);

  const downloadPdf = (kind, doctor_id = null) => {
    const params = new URLSearchParams({ kind, from: range.from, to: range.to });
    if (doctor_id) params.set('doctor_id', doctor_id);
    const url = `${API}/api/management/report.pdf?${params.toString()}`;
    window.open(url, '_blank', 'noopener');
  };

  // ───────── Render ─────────
  const k = overview?.kpis || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-700 via-blue-700 to-cyan-600" />
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: 'linear-gradient(45deg, white 1px, transparent 1px), linear-gradient(-45deg, white 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }} />
        <div className="relative px-8 py-6 flex items-start justify-between text-white">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold opacity-90 mb-1">
              <Sparkles className="h-3 w-3" /> Management Cockpit
            </div>
            <h1 className="text-2xl font-extrabold leading-tight" style={{ fontFamily: 'Manrope' }} data-testid="mgmt-title">
              {tenant?.name || 'Hospital'} — Performance & Operations
            </h1>
            <p className="text-[12px] opacity-85 mt-1">
              {user?.name ? `Signed in as ${user.name}` : 'Management'} · All data shown in IST (Asia/Kolkata)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="w-40 bg-white/15 border-white/30 text-white hover:bg-white/25" data-testid="mgmt-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_PRESETS.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={loadAll}
              className="bg-white/15 border-white/30 text-white hover:bg-white/25" data-testid="mgmt-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button size="sm" onClick={() => navigate('/financials')}
              className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="mgmt-open-financials">
              <IndianRupee className="h-3.5 w-3.5 mr-1.5" /> Financials
            </Button>
            <Button size="sm" onClick={() => downloadPdf('hospital')}
              className="bg-white text-blue-700 hover:bg-blue-50 font-bold" data-testid="mgmt-pdf-hospital">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Hospital PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}
              className="bg-white/0 border-white/0 text-white/85 hover:bg-white/10" data-testid="mgmt-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-[1500px] mx-auto space-y-6">
        {/* KPI ROW */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiTile label="Total Visits"  value={loading ? '…' : k.total_visits ?? 0}
            icon={Stethoscope} hint={`${range.label}`} tone="blue"
            onClick={() => setDrawer({ kind: 'visits' })} />
          <KpiTile label="Revenue"       value={loading ? '…' : inr(k.revenue_total)}
            icon={TrendingUp} hint={`${k.revenue_txn_count || 0} txns`} tone="teal"
            onClick={() => setDrawer({ kind: 'revenue' })} />
          <KpiTile label="Today's Queue" value={loading ? '…' : k.today_queue ?? 0}
            icon={Clock} hint={`${k.today_sessions || 0} sessions`} tone="amber" />
          <KpiTile label="Schedule Rx"   value={loading ? '…' : schedule?.total ?? 0}
            icon={ShieldAlert} hint="H / H1 / X" tone="rose"
            onClick={() => setDrawer({ kind: 'schedule' })} />
        </div>

        {/* CHART */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Visit Volume</div>
              <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>Visits per day · {range.label}</h3>
            </div>
            <div className="text-xs text-zinc-500">All times in Asia/Kolkata</div>
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={overview?.visits_per_day || []} margin={{ top: 5, right: 12, bottom: 12, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#475569' }} />
                <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#eef2ff' }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="visits" fill="#4f46e5" radius={[6, 6, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* DOCTOR WORKLOAD */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Doctor Workload</div>
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>Visits · Rx lines · Hours rostered</h3>
              </div>
              <Button variant="outline" size="sm" onClick={() => setDrawer({ kind: 'doctors' })} data-testid="mgmt-doctors-drilldown">
                Open leaderboard <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm" data-testid="mgmt-doctors-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left py-2">Doctor</th>
                    <th className="text-left">Specialty</th>
                    <th className="text-right">Visits</th>
                    <th className="text-right">Rx lines</th>
                    <th className="text-right">Hrs rostered</th>
                    <th className="text-right">Today queue</th>
                    <th className="text-right">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {(workload?.doctors || []).map((d, i) => (
                    <tr key={d.doctor_id} className="border-t border-zinc-100">
                      <td className="py-2 font-semibold text-zinc-900">
                        {formatDoctorName(d.name)}
                        {i === 0 && <span className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5 align-middle">TOP</span>}
                        {i === (workload?.doctors?.length - 1) && (workload?.doctors?.length > 1) && (
                          <span className="ml-2 text-[10px] bg-zinc-50 text-zinc-600 border border-zinc-200 rounded px-1.5 py-0.5 align-middle">LIGHT</span>
                        )}
                      </td>
                      <td className="text-zinc-600">{d.specialization || '—'}</td>
                      <td className="text-right font-mono">{d.visits}</td>
                      <td className="text-right font-mono">{d.rx_lines}</td>
                      <td className="text-right font-mono">{d.hours_rostered.toFixed(1)}</td>
                      <td className="text-right font-mono">{d.appointments_today}</td>
                      <td className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf('doctor', d.doctor_id)}
                          className="text-blue-700 hover:bg-blue-50" data-testid={`mgmt-doctor-pdf-${d.doctor_id}`}>
                          <FileDown className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {(!workload || workload.doctors?.length === 0) && (
                    <tr><td colSpan="7" className="py-6 text-center text-zinc-400">No doctors yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {workload?.most_visits && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">
                  <TrendingUp className="h-3 w-3" /> Most visits: <b>{formatDoctorName(workload.most_visits.name)}</b> ({workload.most_visits.visits})
                </span>
                {workload.least_visits && workload.doctors.length > 1 && (
                  <span className="inline-flex items-center gap-1 text-[11px] bg-zinc-50 text-zinc-700 border border-zinc-200 rounded-full px-3 py-1">
                    <TrendingDown className="h-3 w-3" /> Least visits: <b>{formatDoctorName(workload.least_visits.name)}</b> ({workload.least_visits.visits})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* AI INSIGHTS */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Co-Pilot</div>
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>AI Insights</h3>
              </div>
              <Button size="sm" variant="outline" onClick={loadAi} disabled={aiLoading} data-testid="mgmt-ai-generate">
                {aiLoading ? 'Thinking…' : (ai ? 'Refresh' : 'Generate')}
              </Button>
            </div>
            {!ai && !aiLoading && (
              <div className="text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg p-4 leading-relaxed">
                Tap <b>Generate</b> to ask the Co-Pilot for observations, risks, recommendations & alerts based on the selected period.
              </div>
            )}
            {aiLoading && (
              <div className="text-sm text-zinc-500 animate-pulse">Asking Claude…</div>
            )}
            {ai && (
              <div className="space-y-3 text-sm" data-testid="mgmt-ai-result">
                <AiBucket label="Observations" tone="blue"  items={ai.sections.OBSERVATIONS} icon={Sparkles} />
                <AiBucket label="Risks"        tone="rose"  items={ai.sections.RISKS}        icon={ShieldAlert} />
                <AiBucket label="Recommendations" tone="emerald" items={ai.sections.RECOMMENDATIONS} icon={Activity} />
                <AiBucket label="Alerts"       tone="amber" items={ai.sections.ALERTS}       icon={AlertTriangle} />
              </div>
            )}
          </div>
        </div>

        {/* REFERRALS + SCHEDULE DRUGS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Referrals */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Care Pathway</div>
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>Referrals · {referrals?.total ?? 0}</h3>
              </div>
              <Button variant="outline" size="sm" onClick={() => setDrawer({ kind: 'referrals' })} data-testid="mgmt-referrals-drilldown">
                See all <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            <SimpleList rows={(referrals?.by_doctor || []).slice(0, 5)} k1="doctor" k2="count" empty="No referrals in this period." testid="mgmt-referrals-bydoctor" />
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-1.5">Top reasons</div>
            <SimpleList rows={(referrals?.by_reason || []).slice(0, 5)} k1="reason" k2="count" empty="—" testid="mgmt-referrals-byreason" />
          </div>

          {/* Schedule drugs */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Pharmacovigilance</div>
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>Schedule-drug Rx · {schedule?.total ?? 0}</h3>
              </div>
              <Button variant="outline" size="sm" onClick={() => setDrawer({ kind: 'schedule' })} data-testid="mgmt-schedule-drilldown">
                See all <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(schedule?.by_schedule || []).map(b => (
                <span key={b.schedule} className="text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-0.5">
                  Schedule {b.schedule} · {b.count}
                </span>
              ))}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">By doctor</div>
            <SimpleList rows={(schedule?.by_doctor || []).slice(0, 5)} k1="doctor" k2="count" empty="None" testid="mgmt-sched-bydoctor" />
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-1.5">By drug class</div>
            <SimpleList rows={(schedule?.by_class || []).slice(0, 5)} k1="class" k2="count" empty="None" testid="mgmt-sched-byclass" />
          </div>
        </div>

        {/* ROSTER */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Operations</div>
              <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>Roster · next 14 days</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => setDrawer({ kind: 'roster' })} data-testid="mgmt-roster-drilldown">
              Open full <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          {(!roster?.shifts || roster.shifts.length === 0) ? (
            <div className="text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg p-4">
              No shifts published in the next 14 days. Reception can add via <b>/reception/roster</b>.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm" data-testid="mgmt-roster-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left py-2">Date (IST)</th>
                    <th className="text-left">Doctor</th>
                    <th className="text-left">Start</th>
                    <th className="text-left">End</th>
                    <th className="text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.shifts.slice(0, 10).map((s, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="py-2 text-zinc-700">{s.date}</td>
                      <td className="font-semibold text-zinc-900">{formatDoctorName(s.doctor)}</td>
                      <td>{s.start}</td>
                      <td>{s.end}</td>
                      <td className="text-zinc-500">{s.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* DRILL-DOWN DRAWER */}
      <DrillDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        workload={workload}
        referrals={referrals}
        schedule={schedule}
        roster={roster}
        overview={overview}
        downloadPdf={downloadPdf}
      />
    </div>
  );
}

const AiBucket = ({ label, items, tone, icon: Icon }) => {
  const tones = {
    blue:    'bg-blue-50 text-blue-900 border-blue-200',
    rose:    'bg-rose-50 text-rose-900 border-rose-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
  }[tone];
  const safe = items && items.length ? items.slice(0, 4) : [];
  return (
    <div className={`rounded-xl border ${tones} p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider mb-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      {safe.length ? (
        <ul className="space-y-1 text-[12.5px] leading-snug">
          {safe.map((s, i) => <li key={i}>• {s}</li>)}
        </ul>
      ) : (
        <div className="text-[11px] opacity-60">No items.</div>
      )}
    </div>
  );
};

const SimpleList = ({ rows, k1, k2, empty, testid }) => {
  if (!rows || rows.length === 0) return <div className="text-xs text-zinc-400">{empty}</div>;
  const max = Math.max(...rows.map(r => r[k2])) || 1;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {rows.map((r, i) => (
        <li key={i} className="flex items-center gap-3 text-sm">
          <span className="w-44 truncate text-zinc-700">{r[k1]}</span>
          <span className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
            <span className="block h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full"
                  style={{ width: `${(r[k2] / max) * 100}%` }} />
          </span>
          <span className="font-mono text-xs text-zinc-700 w-8 text-right">{r[k2]}</span>
        </li>
      ))}
    </ul>
  );
};

function DrillDrawer({ drawer, onClose, workload, referrals, schedule, roster, downloadPdf }) {
  if (!drawer) return null;
  const k = drawer.kind;

  if (k === 'doctors') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Workload" title="Doctor Leaderboard"
        subtitle="Ranked by visits in selected period" accent="#1d4ed8" icon={<Users className="h-5 w-5" />}
        width="xl" testId="mgmt-drawer-doctors">
        <div className="p-5">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="text-left py-2">#</th><th className="text-left">Doctor</th>
              <th className="text-left">Specialty</th><th className="text-right">Visits</th>
              <th className="text-right">Rx</th><th className="text-right">Investigations</th>
              <th className="text-right">Hours</th><th className="text-right">PDF</th>
            </tr></thead>
            <tbody>
              {(workload?.doctors || []).map((d, i) => (
                <tr key={d.doctor_id} className="border-t border-zinc-100">
                  <td className="py-2 text-zinc-400">{i + 1}</td>
                  <td className="font-semibold">{formatDoctorName(d.name)}</td>
                  <td className="text-zinc-600">{d.specialization || '—'}</td>
                  <td className="text-right font-mono">{d.visits}</td>
                  <td className="text-right font-mono">{d.rx_lines}</td>
                  <td className="text-right font-mono">{d.investigations}</td>
                  <td className="text-right font-mono">{d.hours_rostered.toFixed(1)}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf('doctor', d.doctor_id)}>
                      <FileDown className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DrawerShell>
    );
  }

  if (k === 'referrals') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Care Pathway" title="Referrals — drill-down"
        subtitle={`${referrals?.total ?? 0} referrals in this period`} accent="#7c3aed" icon={<ScrollText className="h-5 w-5" />}
        width="lg" testId="mgmt-drawer-referrals">
        <div className="p-5 space-y-4">
          {(referrals?.items || []).length === 0 ? (
            <div className="text-sm text-zinc-500">No referrals captured yet.</div>
          ) : (referrals.items.map((r, i) => (
            <div key={i} className="border border-zinc-200 rounded-xl p-3.5 bg-white">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-zinc-900">{formatDoctorName(r.doctor_name)}</span>
                <span className="text-zinc-500">{r.at ? new Date(r.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</span>
              </div>
              <div className="text-sm mt-1">→ <b className="text-violet-700">{r.referred_to}</b></div>
              <div className="text-xs text-zinc-600 mt-1">Reason: {r.reason}</div>
            </div>
          )))}
        </div>
      </DrawerShell>
    );
  }

  if (k === 'schedule') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Pharmacovigilance" title="Schedule-drug prescriptions"
        subtitle={`${schedule?.total ?? 0} lines flagged — H / H1 / X`} accent="#dc2626" icon={<Pill className="h-5 w-5" />}
        width="lg" testId="mgmt-drawer-schedule">
        <div className="p-5">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">When (IST)</th><th className="text-left">Doctor</th>
                <th className="text-left">Medicine</th><th className="text-left">Class</th>
                <th className="text-left">Schedule</th><th className="text-left">Dose</th>
              </tr></thead>
              <tbody>
                {(schedule?.items || []).map((r, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="py-2 text-zinc-700">{r.at ? new Date(r.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                    <td className="font-semibold">{formatDoctorName(r.doctor_name)}</td>
                    <td>{r.medicine}</td>
                    <td className="text-zinc-600">{r.class}</td>
                    <td><span className="text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 rounded px-1.5 py-0.5">Sch {r.schedule}</span></td>
                    <td className="text-zinc-700">{r.dosage} · {r.duration}</td>
                  </tr>
                ))}
                {(!schedule || schedule.items.length === 0) && (
                  <tr><td colSpan="6" className="py-6 text-center text-zinc-400">None flagged.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DrawerShell>
    );
  }

  if (k === 'roster') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Operations" title="Roster — next 14 days"
        subtitle={`${roster?.from} → ${roster?.to}`} accent="#059669" icon={<Calendar className="h-5 w-5" />}
        width="lg" testId="mgmt-drawer-roster">
        <div className="p-5">
          {(!roster?.shifts || roster.shifts.length === 0) ? (
            <div className="text-sm text-zinc-500">No shifts.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">Date</th><th className="text-left">Doctor</th>
                <th className="text-left">Start</th><th className="text-left">End</th>
              </tr></thead>
              <tbody>
                {roster.shifts.map((s, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="py-2">{s.date}</td>
                    <td className="font-semibold">{formatDoctorName(s.doctor)}</td>
                    <td>{s.start}</td>
                    <td>{s.end}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DrawerShell>
    );
  }

  // visits / revenue: generic info drawer
  return (
    <DrawerShell open onClose={onClose} eyebrow="Detail" title="Period summary"
      accent="#0891b2" icon={<FileBarChart className="h-5 w-5" />} width="md" testId={`mgmt-drawer-${k}`}>
      <div className="p-5 text-sm space-y-2 text-zinc-700">
        <p><b>Range:</b> Asia/Kolkata window applied on backend aggregation.</p>
        <p>This tile drives the daily bar chart in the main view. For per-doctor breakdowns, see the leaderboard.</p>
      </div>
    </DrawerShell>
  );
}
